import styled from "@emotion/styled";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { SearchResultData } from "@kaltura/mediaspace-shared-types";
import { entrySearchResultService, translate, translatePlural, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import {
    Bookmark16Icon,
    Captions24Icon,
    Chat24Icon,
    Notes24Icon,
    Poll24Icon,
    QuestionMark16Icon,
    Quiz24Icon,
    Slide24Icon,
    Tag16Icon,
    Copy24Icon,
} from "@kaltura/ds-react-icons";
import SearchResultItem from "./SearchResultItem";
import { ComponentClass } from "react";
import { useTheme } from "@kaltura/mediaspace-shared-styled";

export interface SearchResultsProps {
    /**
     * class name for the root element
     */
    className: string;
    /**
     * data to show, API search result
     */
    data: KalturaESearchEntryResult;
    /**
     * callback for items click event
     */
    onItemClick: () => void;
}

const StyledSearchResults = styled("div")(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
}));

/**
 * a function that takes an icon and returns it 16px*16px size
 * @param Icon
 */
const IconSizer = (Icon: ComponentClass) =>
    styled(Icon)(({ theme }) => ({
        width: theme.spacing(2),
        height: theme.spacing(2),
    }));

/**
 * get a search result component that matches the given result data
 * function that binds the click handler to a returned function.
 * the inner function returns a component that matches the given search result
 * @param onItemClick
 */
const getComponent = (onItemClick: () => void) => (o: SearchResultData) => {
    let IconClass,
        icon,
        label,
        isTimed = false;
    switch (o.key) {
        case "tags":
            IconClass = IconSizer(Tag16Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Tag", "%1 Tags", [o.totalCount]);
            break;
        case "caption":
            IconClass = IconSizer(Captions24Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Caption", "%1 Captions", [o.totalCount]);
            isTimed = true;
            break;
        case "slides":
            IconClass = IconSizer(Slide24Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Slide", "%1 Slides", [o.totalCount]);
            isTimed = true;
            break;
        case "chapters":
            IconClass = IconSizer(Bookmark16Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Chapter", "%1 Chapters", [o.totalCount]);
            isTimed = true;
            break;
        case "comments":
            IconClass = IconSizer(Chat24Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Comment", "%1 Comments", [o.totalCount]);
            break;
        case "quiz":
            IconClass = IconSizer(Quiz24Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translate("%1 Quiz", [o.totalCount]);
            isTimed = true;
            break;
        case "polls":
            IconClass = IconSizer(Poll24Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Poll", "%1 Polls", [o.totalCount]);
            break;
        case "qna":
            IconClass = IconSizer(QuestionMark16Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Question", "%1 Questions", [o.totalCount]);
            break;
        case "metadata":
            IconClass = IconSizer(Notes24Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translatePlural("%1 Detail", "%1 Details", [o.totalCount]);
            break;
        case "ocr":
            IconClass = IconSizer(Copy24Icon as unknown as ComponentClass);
            icon = <IconClass />;
            label = translate("%1 OCR", [o.totalCount]);
            isTimed = true;
            break;
        default:
            console.log("search results unhandled data item: ");
            console.log(o);
            return null;
    }
    return (
        <SearchResultItem
            key={o.key}
            icon={icon}
            label={label}
            values={o.value}
            onClick={onItemClick}
            isTimed={isTimed}
        />
    );
};

export function SearchResults({ className, data, onItemClick }: SearchResultsProps) {
    const theme = useTheme();
    const isUpMdScreen = useMediaQuery(theme.breakpoints.up("md"));
    const isDownLgScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const showLimited = isUpMdScreen && isDownLgScreen;

    let searchResultsItems = entrySearchResultService.groupItems(data);
    let additional = 0;
    if (showLimited && searchResultsItems.length > 3) {
        additional = searchResultsItems.length - 3;
        searchResultsItems = searchResultsItems.slice(0, 3);
    }
    const components = searchResultsItems.map(getComponent(onItemClick));

    return (
        <StyledSearchResults className={className}>
            {components}
            {!!additional && <span>+ {additional}</span>}
        </StyledSearchResults>
    );
}

export default SearchResults;
